/**
 * Brand Icons Registry
 * Provides brand icons (LinkedIn, Slack, Facebook, X) via local SVG files
 *
 * This registry uses locally hosted SVG files from /public/icons/brands/
 * instead of relying on external CDNs like @iconify/react, ensuring
 * reliable rendering without network dependencies.
 */

import type { ReactNode } from 'react';

import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { BRAND_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Brand Icon Metadata
// ============================================================================

const brandIconMetadata: Record<string, { label: string; alt: string }> = {
  slack: { label: 'Slack', alt: 'Slack logo' },
  linkedin: { label: 'LinkedIn', alt: 'LinkedIn logo' },
  facebook: { label: 'Facebook', alt: 'Facebook logo' },
  x: { label: 'X', alt: 'X (Twitter) logo' },
};

// ============================================================================
// Brand Renderer Implementation
// ============================================================================

const DARK_ICONS = ['x']; // Icons that need light background for visibility

const brandRenderer: IconRenderer = {
  render: (name: string, { size, className }: IconRenderProps): ReactNode => {
    // Validate icon name exists
    if (!(BRAND_ICONS as readonly string[]).includes(name)) {
      return null;
    }

    const metadata = brandIconMetadata[name];
    if (!metadata) {
      return null;
    }

    const iconSize = size || 24;
    const needsBackground = DARK_ICONS.includes(name);

    const img = (
      <img
        src={`/icons/brands/${name}.svg`}
        alt={metadata.alt}
        width={needsBackground ? Math.round(iconSize * 0.8) : iconSize}
        height={needsBackground ? Math.round(iconSize * 0.8) : iconSize}
        className={className}
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      />
    );

    if (needsBackground) {
      return (
        <div
          className="flex items-center justify-center rounded-full bg-white"
          style={{ width: iconSize, height: iconSize }}
        >
          {img}
        </div>
      );
    }

    return img;
  },

  validate: (name: string): boolean => {
    return (BRAND_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    if (!(BRAND_ICONS as readonly string[]).includes(name)) {
      return null;
    }

    const metadata = brandIconMetadata[name];
    if (!metadata) {
      return null;
    }

    return function WrappedBrandIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      const needsBackground = DARK_ICONS.includes(name);

      const img = (
        <img
          src={`/icons/brands/${name}.svg`}
          alt={metadata.alt}
          width={needsBackground ? Math.round(size * 0.8) : size}
          height={needsBackground ? Math.round(size * 0.8) : size}
          className={className}
          style={{
            display: 'inline-block',
            verticalAlign: 'middle',
          }}
        />
      );

      if (needsBackground) {
        return (
          <div
            className="flex items-center justify-center rounded-full bg-white"
            style={{ width: size, height: size }}
          >
            {img}
          </div>
        );
      }

      return img;
    };
  },

  getAll: (): IconDefinition[] =>
    BRAND_ICONS.map(name => {
      const metadata = brandIconMetadata[name];
      return {
        id: `brand:${name}`,
        component: function BrandIconComponent({
          size = 24,
          className,
        }: {
          size?: number;
          className?: string;
        }) {
          const needsBackground = DARK_ICONS.includes(name);

          const img = (
            <img
              src={`/icons/brands/${name}.svg`}
              alt={metadata.alt}
              width={needsBackground ? Math.round(size * 0.8) : size}
              height={needsBackground ? Math.round(size * 0.8) : size}
              className={className}
              style={{
                display: 'inline-block',
                verticalAlign: 'middle',
              }}
            />
          );

          if (needsBackground) {
            return (
              <div
                className="flex items-center justify-center rounded-full bg-white"
                style={{ width: size, height: size }}
              >
                {img}
              </div>
            );
          }

          return img;
        },
        label: metadata.label,
        category: 'brand',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('brand', brandRenderer);

export { brandRenderer };
